import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

export type BalloonKind = 'speech' | 'shout' | 'tired';

/** One line of dialogue. Positions are % of the square panel; tip is where the tail points (near the speaker's head). */
export type Balloon = {
    speaker: 'Luca' | 'Sara';
    text: string;
    kind: BalloonKind;
    x: number;
    y: number;
    /** Maximum width, % of the panel: the balloon shrinks to its text. */
    w: number;
    tip: [number, number];
    /** Seconds after the panel starts when the balloon pops in. */
    cue: number;
};

/** Outline in a 0..100 box: ellipse for speech, ellipse with a slow wobble when tired, starburst for a shout. */
function outline(kind: BalloonKind) {
    const count = kind === 'shout' ? 28 : 72;
    const points: [number, number][] = [];
    for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2;
        let radius = 1;
        if (kind === 'shout') radius = i % 2 ? 0.8 : 1.06 + (i % 4 === 0 ? 0.06 : 0);
        if (kind === 'tired') radius = 1 + Math.sin(angle * 9) * 0.025;
        points.push([50 + Math.cos(angle) * 50 * radius, 50 + Math.sin(angle) * 50 * radius]);
    }
    const fmt = (p: [number, number]) => `${p[0].toFixed(2)} ${p[1].toFixed(2)}`;
    if (kind === 'shout') return `M${points.map(fmt).join(' L')} Z`;
    // Smooth closed curve through the points (Catmull-Rom converted to cubic Bézier).
    let d = `M${fmt(points[0])}`;
    for (let i = 0; i < count; i++) {
        const p0 = points[(i - 1 + count) % count], p1 = points[i], p2 = points[(i + 1) % count], p3 = points[(i + 2) % count];
        const c1: [number, number] = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
        const c2: [number, number] = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
        d += ` C${fmt(c1)} ${fmt(c2)} ${fmt(p2)}`;
    }
    return `${d} Z`;
}

const OUTLINES: Record<BalloonKind, string> = { speech: outline('speech'), shout: outline('shout'), tired: outline('tired') };

type Tail = { fill: string; edge: string };

/** Pixels the tail reaches below the balloon at least. */
const MIN_TAIL_BELOW = 12;

/**
 * Curved tail in the balloon's own pixel space: it starts inside the balloon (the white fill hides the join)
 * and ends at the tip. Only the two sides are stroked, so the balloon outline stays open where the tail leaves.
 */
function tailPath(balloon: Balloon, box: HTMLElement, panelWidth: number, panelHeight: number): Tail {
    const width = box.offsetWidth, height = box.offsetHeight;
    const tipX = balloon.tip[0] / 100 * panelWidth - box.offsetLeft;
    // On small panels the balloon can grow past the tip: the tail always leaves the balloon downwards.
    const tipY = Math.max(balloon.tip[1] / 100 * panelHeight - box.offsetTop, box.offsetHeight + MIN_TAIL_BELOW);
    const baseX = Math.min(Math.max(tipX, width * 0.3), width * 0.7);
    const baseY = height * (balloon.kind === 'shout' ? 0.72 : 0.84);
    const half = balloon.kind === 'shout' ? 9 : 7;
    const controlX = baseX + (tipX - baseX) * 0.45, controlY = baseY + (tipY - baseY) * 0.55;
    const n = (v: number) => v.toFixed(1);
    const sides = `M${n(baseX - half)} ${n(baseY)} Q${n(controlX - half * 0.4)} ${n(controlY)} ${n(tipX)} ${n(tipY)} Q${n(controlX + half * 0.4)} ${n(controlY)} ${n(baseX + half)} ${n(baseY)}`;
    return { fill: `${sides} Z`, edge: sides };
}

/** The balloons of one panel. `balloons` must be a stable array (module data), it drives the measuring effect. */
export default function ComicBalloons({ balloons }: { balloons: Balloon[] }) {
    const layer = useRef<HTMLDivElement>(null);
    const boxes = useRef<(HTMLParagraphElement | null)[]>([]);
    const [tails, setTails] = useState<(Tail | null)[]>([]);

    // Tails depend on the laid-out balloon size: measure after layout, when fonts arrive and when the panel resizes.
    // offset* values ignore the pop-in transform, so the tail is right during the animation too.
    useLayoutEffect(() => {
        const element = layer.current;
        if (!element) return;
        let cancelled = false;
        const draw = () => {
            if (cancelled || !element.clientWidth) return;
            setTails(balloons.map((balloon, index) => {
                const box = boxes.current[index];
                return box ? tailPath(balloon, box, element.clientWidth, element.clientHeight) : null;
            }));
        };
        draw();
        const observer = new ResizeObserver(draw);
        observer.observe(element);
        void document.fonts?.ready.then(draw);
        return () => { cancelled = true; observer.disconnect(); };
    }, [balloons]);

    return <div className="sp-comic__balloons" ref={layer}>
        {balloons.map((balloon, index) => <p
            key={balloon.speaker}
            ref={element => { boxes.current[index] = element; }}
            className={`sp-balloon sp-balloon--${balloon.kind}`}
            style={{ left: `${balloon.x}%`, top: `${balloon.y}%`, '--sp-balloon-width': `${balloon.w}%`, animationDelay: `${balloon.cue}s` } as CSSProperties}
        >
            <svg className="sp-balloon__shape" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"><path d={OUTLINES[balloon.kind]} /></svg>
            {tails[index] && <svg className="sp-balloon__tail" aria-hidden="true"><path className="sp-balloon__tail-fill" d={tails[index]!.fill} /><path className="sp-balloon__tail-edge" d={tails[index]!.edge} /></svg>}
            <span className="sp-balloon__text"><span className="sp-sr-only">{balloon.speaker}: </span>{balloon.text}</span>
        </p>)}
    </div>;
}
